import { Badge, Box, Button, Tooltip } from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useEffect, useMemo, useState } from 'react';
import { useFilters, useFiltersDispatch } from '../TrackerFilter/Context/FiltersContext';
import TrackerFilterPopover from '../TrackerFilter/TrackerFilterPopover';
import { filterStorageTypeMap, getTrackerViewColor, TrackerDataType } from '../../shared/constants/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState, UserState } from '../../redux-types';
import { clearFiltersRequest, getTrackerFilterFromStoredFilter } from '../TrackerFilter/trackerFilterHelpers';
import * as UserActions from '../Authorization/userSlice';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getFuzzySearchServices } from '../../shared/requests/serviceNetworkRequests';
import { getFuzzySearchInteractions } from '../../shared/requests/interactionsRequests';
import { getFuzzySearchEngagements } from '../../shared/requests/engagementsRequest';
import { getFuzzySearchContentLogs } from '../../shared/requests/contentLogsRequest';
import dayjs from '../../utils/dayjs';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import {
  trackerToolbarButtonTypographySx,
  trackerToolbarNeutralHoverFill,
  trackerToolbarNeutralOutlineColor,
  trackerToolbarNeutralTextColor,
} from '../TrackerFilter/trackerFilterToolbarStyles';
import { logPalette } from '../../theme/palette';

interface TrackerTableFilterProps {
  setDataCallback: (data: any) => void;
  eventHandler: any;
  dropdownTable: any;
}

const areDateFilterValuesEqual = (left: Date, right: Date): boolean => left.getTime() === right.getTime();

const areArrayFilterValuesEqual = (left: any[], right: any[]): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((leftValue, index) => areFilterValuesEqual(leftValue, right[index]));
};

const isComparableFilterObject = (value: any): boolean => {
  return value && typeof value === 'object' && !Array.isArray(value);
};

const areObjectFilterValuesEqual = (left: Record<string, any>, right: Record<string, any>): boolean => {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => Object.hasOwn(right, key) && areFilterValuesEqual(left[key], right[key]));
};

const areFilterValuesEqual = (left: any, right: any): boolean => {
  if (left === right) {
    return true;
  }

  if (left instanceof Date && right instanceof Date) {
    return areDateFilterValuesEqual(left, right);
  }

  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && areArrayFilterValuesEqual(left, right);
  }

  if (isComparableFilterObject(left) && isComparableFilterObject(right)) {
    return areObjectFilterValuesEqual(left, right);
  }

  return false;
};

export default function TrackerTableFilter(props: Readonly<TrackerTableFilterProps>) {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();

  const { setDataCallback, eventHandler, dropdownTable } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pollUntil, setPollUntil] = useState<number | null>(null);
  const [inProgress, setInProgress] = useState(false);

  const currentUser = useSelector<StoreState, UserState>((state) => state.user);
  const trackerType = (Object.values(TrackerDataType) as string[]).includes(eventHandler?.type)
    ? (eventHandler.type as TrackerDataType)
    : TrackerDataType.SERVICE;
  const filterStorageTrackerType: keyof typeof filterStorageTypeMap =
    trackerType === TrackerDataType.MEDIA_INTERACTION ||
    trackerType === TrackerDataType.SERVICE ||
    trackerType === TrackerDataType.ENGAGEMENT ||
    trackerType === TrackerDataType.CONTENT_LOG
      ? trackerType
      : TrackerDataType.SERVICE;

  const componentColor = getTrackerViewColor(trackerType);
  const buttonOutlineColor = trackerToolbarNeutralOutlineColor;
  const buttonLabelColor = trackerToolbarNeutralTextColor;
  const buttonSoftFill = 'transparent';
  const buttonSoftFillHover = trackerToolbarNeutralHoverFill;
  const serviceBlueIconColor = logPalette.service.main;

  const filters = useFilters();
  const filtersDispatch = useFiltersDispatch();
  const filterCount = Object.keys(filters).length;

  const dispatch = useDispatch();
  const clearFiltersMutation = useMutation({
    mutationFn: clearFiltersRequest,
    onError: (err) => {
      console.error(err);
    },
  });

  const fuzzySearchEnabled = useMemo(
    () => Boolean(state?.queryString && state?.category),
    [state?.queryString, state?.category]
  );
  const filtersDisabled = fuzzySearchEnabled;

  useEffect(() => {
    if (fuzzySearchEnabled) {
      setPollUntil(dayjs().add(10, 'second').valueOf());
    } else {
      setPollUntil(null);
    }
  }, [fuzzySearchEnabled, state?.queryString, state?.category]);

  useEffect(() => {
    if (pollUntil === null) {
      return;
    }
    const now = dayjs().valueOf();
    const remainingMs = pollUntil - now;
    if (remainingMs <= 0) {
      setPollUntil(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      setPollUntil(null);
    }, remainingMs);
    return () => clearTimeout(timeoutId);
  }, [pollUntil]);

  const fuzzySearchQuery = useQuery({
    queryKey: ['fuzzySearch', eventHandler.type, state?.queryString, state?.category],
    queryFn: async () => {
      switch (eventHandler.type) {
        case TrackerDataType.MEDIA_INTERACTION: {
          const interactionsData = await getFuzzySearchInteractions(state?.queryString, state?.category);
          return interactionsData || null;
        }
        case TrackerDataType.ENGAGEMENT: {
          const engagementsData = await getFuzzySearchEngagements(state?.queryString, state?.category);
          return engagementsData || null;
        }
        case TrackerDataType.CONTENT_LOG: {
          const contentLogsData = await getFuzzySearchContentLogs(state?.queryString, state?.category);
          return contentLogsData || null;
        }
        default: {
          const servicesData = await getFuzzySearchServices(state?.queryString, state?.category);
          return servicesData || null;
        }
      }
    },
    enabled: fuzzySearchEnabled,
    refetchInterval: () => {
      if (!fuzzySearchEnabled || pollUntil === null) {
        return false;
      }
      return dayjs().add(2, 'second').valueOf() < pollUntil ? 2000 : false;
    },
  });

  useEffect(() => {
    if (!fuzzySearchEnabled || !fuzzySearchQuery.isFetching || pollUntil !== null) {
      return;
    }
    setPollUntil(dayjs().add(10, 'second').valueOf());
  }, [fuzzySearchEnabled, fuzzySearchQuery.isFetching, pollUntil]);

  async function clearFiltersOnClick() {
    dispatch(
      UserActions.setUserStoredFilters({
        [filterStorageTypeMap[filterStorageTrackerType]]: null,
      })
    );
    clearFiltersMutation.mutate(eventHandler.type);
  }

  const getBasePath = () => {
    switch (eventHandler.type) {
      case TrackerDataType.MEDIA_INTERACTION:
        return RouteInformation.mediaInteraction.path;
      case TrackerDataType.ENGAGEMENT:
        return RouteInformation.engagementLog.path;
      case TrackerDataType.CONTENT_LOG:
        return RouteInformation.contentLog.path;
      default:
        return RouteInformation.serviceLog.path;
    }
  };
  const basePath = getBasePath();
  const isDetailIdRoute = pathname !== basePath && pathname.startsWith(`${basePath}/`);

  const getFilterButtonIconSx = () => {
    if (filterCount > 0) {
      return { color: serviceBlueIconColor };
    }
    return { color: serviceBlueIconColor };
  };

  const handleFiltersButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFiltersPopoverClose = () => {
    setAnchorEl(null);
  };

  const shouldDisplayClearAllButton = () => {
    return Object.keys(filters).length > 0;
  };

  const handleClearAllFilters = () => {
    setDataCallback(null);
    clearFiltersOnClick();
    filtersDispatch({ type: 'setFilters', payload: {} });
    if (pathname !== basePath || state) {
      navigate(basePath, { replace: true });
    }
  };

  useEffect(() => {
    if (state?.queryString) {
      if (fuzzySearchQuery.isFetching && fuzzySearchQuery.data === undefined) {
        setDataCallback(null);
        return;
      }
      if (!fuzzySearchQuery.isFetching && fuzzySearchQuery.data !== undefined) {
        setDataCallback(fuzzySearchQuery.data);
        return;
      }
    }
    if (fuzzySearchQuery.error) {
      alert(fuzzySearchQuery.error);
      if (fuzzySearchQuery.data === undefined) {
        setDataCallback(null);
      }
    }
  }, [setDataCallback, state?.queryString, fuzzySearchQuery.isFetching, fuzzySearchQuery.data, fuzzySearchQuery.error]);

  const savedFilters = useMemo(
    () =>
      isDetailIdRoute
        ? null
        : currentUser.userInfo.nonPIUserSettings?.userStoredFilters?.[filterStorageTypeMap[filterStorageTrackerType]],
    [isDetailIdRoute, currentUser.userInfo.nonPIUserSettings, filterStorageTrackerType]
  );

  const parsedSavedFilters = useMemo(() => {
    if (!savedFilters || !dropdownTable) {
      return null;
    }
    return getTrackerFilterFromStoredFilter(savedFilters, dropdownTable, eventHandler.type);
  }, [savedFilters, dropdownTable, eventHandler.type]);

  useEffect(() => {
    if (isDetailIdRoute) {
      return;
    }
    if (!parsedSavedFilters) {
      return;
    }

    if (Object.keys(parsedSavedFilters).length === 0) {
      dispatch(
        UserActions.setUserStoredFilters({
          [filterStorageTypeMap[filterStorageTrackerType]]: null,
        })
      );
      return;
    }

    if (!areFilterValuesEqual(filters, parsedSavedFilters)) {
      filtersDispatch({ type: 'setFilters', payload: parsedSavedFilters });
    }
  }, [isDetailIdRoute, parsedSavedFilters, filters, dispatch, filtersDispatch, filterStorageTrackerType]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap' }}>
      <Badge
        sx={{ height: '32px' }}
        badgeContent={Object.keys(filters).length}
        color="error"
        invisible={filterCount === 0}
      >
        <Button
          sx={{
            height: '32px',
            color: buttonLabelColor,
            borderColor: buttonOutlineColor,
            backgroundColor: buttonSoftFill,
            '&:hover': {
              borderColor: buttonOutlineColor,
              backgroundColor: buttonSoftFillHover,
            },
            '& .MuiButton-startIcon .MuiSvgIcon-root': {
              color: serviceBlueIconColor,
            },
            ...trackerToolbarButtonTypographySx,
          }}
          color="whiteScale"
          variant="outlined"
          onClick={handleFiltersButtonClick}
          disabled={filtersDisabled}
          startIcon={<TuneIcon sx={getFilterButtonIconSx()} />}
          data-cy="tracker-filter-btn"
        >
          Filters
        </Button>
      </Badge>
      <Tooltip title={'My Entries In Progress'} placement="top" enterDelay={500}>
        <Button
          sx={{
            height: '32px',
            whiteSpace: 'nowrap',
            minWidth: 'max-content',
            color: buttonLabelColor,
            borderColor: buttonOutlineColor,
            backgroundColor: buttonSoftFill,
            '&:hover': {
              borderColor: buttonOutlineColor,
              backgroundColor: buttonSoftFillHover,
            },
            '& .MuiButton-startIcon .MuiSvgIcon-root': {
              color: serviceBlueIconColor,
            },
            ...trackerToolbarButtonTypographySx,
          }}
          color="whiteScale"
          variant="outlined"
          onClick={() => setInProgress(true)}
          disabled={filtersDisabled}
          startIcon={<TaskAltIcon sx={getFilterButtonIconSx()} />}
          data-cy="tracker-table-filter-in-progress-btn"
        >
          In Progress
        </Button>
      </Tooltip>
      {shouldDisplayClearAllButton() && (
        <Button
          sx={{
            height: '32px',
            whiteSpace: 'nowrap',
            minWidth: 'max-content',
            flexShrink: 0,
            color: buttonLabelColor,
            '&:hover': {
              backgroundColor: buttonSoftFillHover,
            },
            ...trackerToolbarButtonTypographySx,
          }}
          variant="text"
          color={componentColor}
          onClick={() => handleClearAllFilters()}
          data-cy="tracker-filter-clear-all-btn"
        >
          Clear All Filters
        </Button>
      )}
      <TrackerFilterPopover
        anchorEl={anchorEl}
        onClose={handleFiltersPopoverClose}
        setDataCallback={setDataCallback}
        eventHandler={eventHandler}
        inProgress={inProgress}
        inProgressCallback={setInProgress}
      />
    </Box>
  );
}
